import appConstants from '@appConstants';
import { IBETARenderer } from '@dataGrid/components/cellRenderer';
import { DateTimeFilter, MultiSelectFilter } from '@dataGrid/components/columnFilters';
import { formatDeliveryWindow } from '@dataGrid/gridUtilities';
import CheckBoxRenderer from '../../CheckBoxRenderer';
import {
  checkIfProSelectedForCityRoute,
  checkIfAllProsSelectedForCityRoute,
  handleCityRouteCheckboxToggle,
  handleCityRouteCheckboxToggleForAll
} from '../unappointedProsUtils';
import { getCustomerName, ibEtaValueFormatter } from '../../../../../../../modules/IBPlanning/ShipmentsView/utils';
import { UNAPPOINTED_PROS_CONTENT } from './constants';

export default class GridConfig {
  constructor(configuration?: Record<string, unknown>) {
    const config = configuration || {};
    this.name = 'GridConfiguration';
    this.config = GridConfig.getDefaultConfiguration(config);
  }

  name: string;
  config: ReturnType<typeof GridConfig.getDefaultConfiguration>;

  static getConfiguration(config?: Record<string, unknown>) {
    return GridConfig.getDefaultConfiguration(config);
  }

  static getDefaultConfiguration(config?: Record<string, unknown>) {
    return {
      title: UNAPPOINTED_PROS_CONTENT.gridTitle,
      rowSelection: 'multiple',
      rowDeselection: true,
      defaultColDef: {
        sortable: true,
        filter: true
      },
      columns: [
        {
          headerName: '',
          cellRenderer: CheckBoxRenderer,
          headerComponent: CheckBoxRenderer,
          valueGetter: (params: { api: { selectedProsForCityRoute?: Set<string> }; data: { proId: string } }) =>
            checkIfProSelectedForCityRoute(params),
          cellRendererParams: (params: {
            api: { toggleProsForCityRoute?: (proId: string, isSelected: boolean) => void };
            data: { proId: string };
          }) => ({
            onChange: (isChecked: boolean) => handleCityRouteCheckboxToggle(params, isChecked)
          }),
          headerComponentParams: (params: {
            api: {
              forEachNode: (cb: (node: { data: { proId: string } }) => void) => void;
              toggleProsForCityRoute?: (proId: string, isSelected: boolean) => void;
            };
          }) => ({
            value: checkIfAllProsSelectedForCityRoute(params),
            onChange: (isChecked: boolean) => handleCityRouteCheckboxToggleForAll(params, isChecked)
          }),
          headerClass: 'checkBoxHeader',
          cellClassRules: {
            'gridCell-centerAlign': true
          },
          pinned: 'left',
          lockPosition: true,
          lockPinned: true,
          isInvisibleInShowHide: true,
          minWidth: appConstants.columnWidth.W_64,
          maxWidth: appConstants.columnWidth.W_64
        },
        {
          headerName: 'Pro#',
          headerTooltip: 'Pro ID',
          minWidth: appConstants.columnWidth.PROID,
          headerClass: 'headerCellStyle',
          filter: MultiSelectFilter,
          filterParams: {
            filterType: appConstants.FILTER_TYPES.STRING
          },
          field: 'proId'
        },
        {
          headerName: 'Customer',
          headerTooltip: 'Customer Name',
          minWidth: appConstants.columnWidth.CUSTOMER_NAME,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          headerClass: 'headerCellStyle',
          valueGetter: getCustomerName,
          tooltipValueGetter: (params: { value: string }) => params.value
        },
        {
          headerName: 'Shipment Status',
          headerTooltip: 'Shipment Status',
          field: 'shipmentStatus',
          minWidth: appConstants.columnWidth.SHIPMENT_STATUS,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          tooltipField: 'shipmentStatus'
        },
        {
          headerName: 'Delivery Window',
          headerTooltip: 'Delivery Window',
          field: 'deliveryWindow',
          minWidth: appConstants.columnWidth.DELIVERY_WINDOW,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          valueFormatter: formatDeliveryWindow,
          tooltipValueGetter: (params: { value: string }) => params.value
        },
        {
          headerName: 'Curr Term',
          headerTooltip: 'Current Terminal',
          field: 'currentTerminal',
          minWidth: appConstants.columnWidth.TERMINAL,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'IB ETA',
          headerTooltip: 'Shipment ETA',
          field: 'shipmentEtaDateTime',
          minWidth: appConstants.columnWidth.IBETA,
          cellClass: 'gridCell-leftAlign',
          valueFormatter: ibEtaValueFormatter,
          cellRenderer: IBETARenderer,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: appConstants.DATETYPES.DATETIME
          }
        }
      ],
      ...config
    };
  }
}
